import {Injectable} from '@angular/core'
import {HttpClient, HttpParams} from '@angular/common/http'
import {Router} from '@angular/router'

import {Observable, of} from 'rxjs'

import {AuthToken} from './auth-token.interface'
import {LoggerService} from '@app/core/services/logger.service'

export {AuthToken} from './auth-token.interface'

/**
 * Who-am-I answer from `GET Whoami` — the identity the legacy login response
 * used to provide (id + domain drive the portal's domain/user-scoped URLs;
 * see ContextService). Shape verified against the live dev endpoint,
 * 2026-07-16.
 */
export interface SsoUser {
  id: number
  domain: string
  username: string
  emailAddress?: string
}

@Injectable({providedIn: 'root'})
export class AuthService {
  constructor(
    private router: Router,
    private http: HttpClient,
    private logger: LoggerService,
  ) {}

  postAuthentication(
    user: string,
    password: string,
    domain: string,
  ): Observable<AuthToken> {
    const credentials = {username: user, password: password, domain: domain}
    const params = new HttpParams().set('advanced', 'true')
    return this.http.post<AuthToken>('api://Auth', credentials, {
      params: params,
    })
  }

  getToken(tokenId: string): any {
    const url: string = 'api://Auth/' + tokenId
    const params = new HttpParams().set('advanced', 'true')
    return this.http.get<any>(url, {
      params: params,
    })
  }

  /**
   * Who-am-I for an SSO session: the backend resolves the user from the
   * `Authorization: Bearer <keycloak jwt>` header that `ApiAuthInterceptor`
   * attaches — no ids in the URL. Top-level route on purpose: paths under
   * `Auth/` are swallowed by the legacy `Auth/{tokenId}` wildcard.
   */
  getSsoUser(): Observable<SsoUser> {
    return this.http.get<SsoUser>('api://Whoami')
  }

  refreshToken(tokenId: string, refreshToken: string): Observable<AuthToken> {
    const url: string = 'api://Auth/' + tokenId + '/Refresh'
    const body = {refreshToken: refreshToken}
    return this.http.post<AuthToken>(url, body)
  }

  deleteToken(tokenId: string): Observable<void> {
    const url: string = 'api://Auth/' + tokenId
    return this.http.delete<void>(url)
  }

  getExperationTimestamp(token: AuthToken): Observable<number> {
    // const url: string = 'api://Auth/' + tokenId;
    return of(token.expiresIn)
  }

  postPasswordReset(
    email: string,
    domain?: string,
    theme: string = 'default',
  ): Observable<void> {
    const url = 'api://Auth/PasswordReset'
    const request = {
      email: email,
      domain: domain,
      theme: theme,
    }
    return this.http.post<void>(url, request)
  }

  putPasswordReset(identifier: string, password: string): Observable<void> {
    const url = 'api://Auth/PasswordReset/' + identifier
    const update = {
      password: password,
    }
    return this.http.put<void>(url, update)
  }
}
